import { Router, Route } from '@solidjs/router';
import {
  lazy,
  Suspense,
  type Component,
  type ParentProps,
  onMount,
} from 'solid-js';
import { AuthOnlyRoute } from '@/features/auth/components/AuthOnlyRoute';
import { ToastContainer } from '@/shared/ui';
import { AppErrorBoundary } from '@/shared/components/ErrorBoundary';
import { setLocale } from '@/shared/i18n';
import { RouteSeo } from './seo/RouteSeo';

// Auth pages — lazy loaded to keep startup bundle focused on the active route.
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
const SignupPage = lazy(() => import('@/features/auth/pages/SignupPage'));
const OnboardingPage = lazy(
  () => import('@/features/auth/pages/OnboardingPage')
);
const ForgotPasswordPage = lazy(
  () => import('@/features/auth/pages/ForgotPasswordPage')
);
const ResetPasswordPage = lazy(
  () => import('@/features/auth/pages/ResetPasswordPage')
);
const NoBusinessPage = lazy(
  () => import('@/features/auth/pages/NoBusinessPage')
);
const SetupBusinessPage = lazy(
  () => import('@/features/auth/pages/SetupBusinessPage')
);
import LandingPage from './pages/public/LandingPage';

// Lazy load app pages (only needed once authenticated)
const ProfilePage = lazy(() => import('@/features/profile/pages/ProfilePage'));
const HomePage = lazy(() => import('./pages/HomePage'));
const InventoryPage = lazy(() => import('./pages/InventoryPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const ImportsPage = lazy(() => import('./pages/ImportsPage'));
const DataImportPage = lazy(() => import('./pages/DataImportPage'));
const ActivityLogPage = lazy(() => import('./pages/ActivityLogPage'));
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'));
const ClientsPage = lazy(() => import('./pages/ClientsPage'));
const SuppliersPage = lazy(() => import('./pages/SuppliersPage'));
const TransfersPage = lazy(() => import('./pages/TransfersPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const TeamManagementPage = lazy(() => import('./pages/TeamManagementPage'));
const BillingPage = lazy(() => import('@/features/billing/pages/BillingPage'));
const AnalyticsPage = lazy(
  () => import('@/features/analytics/pages/AnalyticsPage')
);
const RiskAnalyticsPage = lazy(
  () => import('@/features/analytics/pages/RiskAnalyticsPage')
);
const StockIntelligencePage = lazy(
  () => import('@/features/analytics/pages/StockIntelligencePage')
);
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const DevPortalPage = lazy(() => import('@/features/dev/pages/DevPortalPage'));
const DevRequestsPage = lazy(
  () => import('@/features/dev/pages/DevRequestsPage')
);
const TranslationPortalPage = lazy(
  () => import('@/features/translations/pages/TranslationPortalPage')
);

// Public pages (secondary — lazy since not on critical path)
const PricingPage = lazy(() => import('./pages/public/PricingPage'));
const PrivacyPolicyPage = lazy(
  () => import('./pages/public/PrivacyPolicyPage')
);
const TermsOfServicePage = lazy(
  () => import('./pages/public/TermsOfServicePage')
);
const BillingTermsPage = lazy(() => import('./pages/public/BillingTermsPage'));
const InventoryManagementPage = lazy(
  () => import('./pages/public/InventoryManagementPage')
);
const AiAnalyticsPage = lazy(() => import('./pages/public/AiAnalyticsPage'));
const BarcodeMobilePage = lazy(
  () => import('./pages/public/BarcodeMobilePage')
);
const TeamPermissionsPage = lazy(
  () => import('./pages/public/TeamPermissionsPage')
);
const BlogsIndexPage = lazy(
  () => import('./pages/public/blogs/BlogsIndexPage')
);
const SpreadsheetTrapPost = lazy(
  () => import('./pages/public/blogs/SpreadsheetTrapPost')
);
const WalkingTaxPost = lazy(
  () => import('./pages/public/blogs/WalkingTaxPost')
);
const GhostInventoryPost = lazy(
  () => import('./pages/public/blogs/GhostInventoryPost')
);
const BlogsIndexPageVi = lazy(
  () => import('./pages/public/blogs/BlogsIndexPageVi')
);
const SpreadsheetTrapPostVi = lazy(
  () => import('./pages/public/blogs/SpreadsheetTrapPostVi')
);
const WalkingTaxPostVi = lazy(
  () => import('./pages/public/blogs/WalkingTaxPostVi')
);
const GhostInventoryPostVi = lazy(
  () => import('./pages/public/blogs/GhostInventoryPostVi')
);
const GrowthTrapPost = lazy(
  () => import('./pages/public/blogs/GrowthTrapPost')
);
const CrystalBallPost = lazy(
  () => import('./pages/public/blogs/CrystalBallPost')
);
const GrowthTrapPostVi = lazy(
  () => import('./pages/public/blogs/GrowthTrapPostVi')
);
const CrystalBallPostVi = lazy(
  () => import('./pages/public/blogs/CrystalBallPostVi')
);
// ProtectedLayout is NOT lazy — lazy() causes a race with the router's
// startTransition: when logout fires and the router navigates to /login, the
// lazy import's internal createResource can resolve after the Show has already
// unmounted the component tree, causing an insertBefore NotFoundError.
import ProtectedLayout from './layouts/ProtectedLayout';

/**
 * Wrapper that sets locale to Vietnamese on mount.
 * Used for /vi/* public routes so the page renders in Vietnamese without
 * requiring the user to manually switch language.
 */
const ViPublicWrapper: Component<ParentProps> = (props) => {
  onMount(() => setLocale('vi'));
  return <>{props.children}</>;
};

export function AppRouter() {
  return (
    <AppErrorBoundary>
      <Router>
        <RouteSeo />
        <Suspense
          fallback={
            <div class="flex h-screen items-center justify-center bg-white">
              <div class="text-gray-400">Loading…</div>
            </div>
          }
        >
          {/* Public routes — nested Suspense with null fallback so lazy-chunk loading
              doesn't flash the dark app-shell background */}
          <Suspense fallback={null}>
            <Route path="/welcome" component={LandingPage} />
            <Route path="/pricing" component={PricingPage} />
            <Route
              path="/features/inventory-management"
              component={InventoryManagementPage}
            />
            <Route path="/features/ai-analytics" component={AiAnalyticsPage} />
            <Route
              path="/features/barcode-mobile"
              component={BarcodeMobilePage}
            />
            <Route
              path="/features/team-permissions"
              component={TeamPermissionsPage}
            />
            <Route path="/privacy" component={PrivacyPolicyPage} />
            <Route path="/terms" component={TermsOfServicePage} />
            <Route path="/billing-terms" component={BillingTermsPage} />
            <Route path="/blogs" component={BlogsIndexPage} />
            <Route
              path="/blogs/spreadsheet-trap"
              component={SpreadsheetTrapPost}
            />
            <Route path="/blogs/walking-tax" component={WalkingTaxPost} />
            <Route
              path="/blogs/ghost-inventory"
              component={GhostInventoryPost}
            />
            <Route path="/blogs/growth-trap" component={GrowthTrapPost} />
            <Route path="/blogs/crystal-ball" component={CrystalBallPost} />
            {/* Vietnamese public routes — /vi/* mirrors EN routes with locale forced to 'vi' */}
            <Route
              path="/vi/welcome"
              component={() => (
                <ViPublicWrapper>
                  <LandingPage />
                </ViPublicWrapper>
              )}
            />
            <Route
              path="/vi/pricing"
              component={() => (
                <ViPublicWrapper>
                  <PricingPage />
                </ViPublicWrapper>
              )}
            />
            <Route
              path="/vi/features/inventory-management"
              component={() => (
                <ViPublicWrapper>
                  <InventoryManagementPage />
                </ViPublicWrapper>
              )}
            />
            <Route
              path="/vi/features/ai-analytics"
              component={() => (
                <ViPublicWrapper>
                  <AiAnalyticsPage />
                </ViPublicWrapper>
              )}
            />
            <Route
              path="/vi/features/barcode-mobile"
              component={() => (
                <ViPublicWrapper>
                  <BarcodeMobilePage />
                </ViPublicWrapper>
              )}
            />
            <Route
              path="/vi/features/team-permissions"
              component={() => (
                <ViPublicWrapper>
                  <TeamPermissionsPage />
                </ViPublicWrapper>
              )}
            />
            <Route
              path="/vi/privacy"
              component={() => (
                <ViPublicWrapper>
                  <PrivacyPolicyPage />
                </ViPublicWrapper>
              )}
            />
            <Route
              path="/vi/terms"
              component={() => (
                <ViPublicWrapper>
                  <TermsOfServicePage />
                </ViPublicWrapper>
              )}
            />
            <Route
              path="/vi/billing-terms"
              component={() => (
                <ViPublicWrapper>
                  <BillingTermsPage />
                </ViPublicWrapper>
              )}
            />{' '}
            <Route
              path="/vi/blogs"
              component={() => (
                <ViPublicWrapper>
                  <BlogsIndexPageVi />
                </ViPublicWrapper>
              )}
            />
            <Route
              path="/vi/blogs/spreadsheet-trap"
              component={() => (
                <ViPublicWrapper>
                  <SpreadsheetTrapPostVi />
                </ViPublicWrapper>
              )}
            />
            <Route
              path="/vi/blogs/walking-tax"
              component={() => (
                <ViPublicWrapper>
                  <WalkingTaxPostVi />
                </ViPublicWrapper>
              )}
            />
            <Route
              path="/vi/blogs/ghost-inventory"
              component={() => (
                <ViPublicWrapper>
                  <GhostInventoryPostVi />
                </ViPublicWrapper>
              )}
            />
            <Route
              path="/vi/blogs/growth-trap"
              component={() => (
                <ViPublicWrapper>
                  <GrowthTrapPostVi />
                </ViPublicWrapper>
              )}
            />{' '}
            <Route
              path="/vi/blogs/crystal-ball"
              component={() => (
                <ViPublicWrapper>
                  <CrystalBallPostVi />
                </ViPublicWrapper>
              )}
            />
          </Suspense>
          <Route path="/login" component={LoginPage} />
          <Route path="/signup" component={SignupPage} />
          <Route path="/onboarding" component={OnboardingPage} />
          <Route path="/forgot-password" component={ForgotPasswordPage} />
          <Route path="/reset-password" component={ResetPasswordPage} />
          <Route
            path="/no-business"
            component={() => (
              <AuthOnlyRoute>
                <NoBusinessPage />
              </AuthOnlyRoute>
            )}
          />
          <Route
            path="/setup"
            component={() => (
              <AuthOnlyRoute>
                <SetupBusinessPage />
              </AuthOnlyRoute>
            )}
          />
          {/* Protected routes with layout */}
          <Route path="/" component={ProtectedLayout}>
            <Route path="/" component={HomePage} />
            <Route path="/inventory" component={InventoryPage} />
            <Route path="/activity" component={ActivityLogPage} />
            <Route path="/audit-log" component={AuditLogPage} />
            <Route path="/orders" component={OrdersPage} />
            <Route path="/imports" component={ImportsPage} />
            <Route path="/data-import" component={DataImportPage} />
            <Route path="/transfers" component={TransfersPage} />
            <Route path="/clients" component={ClientsPage} />
            <Route path="/suppliers" component={SuppliersPage} />
            <Route path="/settings" component={SettingsPage} />
            <Route path="/team" component={TeamManagementPage} />
            <Route path="/billing" component={BillingPage} />
            <Route path="/analytics" component={AnalyticsPage} />
            <Route path="/analytics/risk" component={RiskAnalyticsPage} />
            <Route
              path="/analytics/stock-intelligence"
              component={StockIntelligencePage}
            />
            <Route path="/profile" component={ProfilePage} />
            <Route path="/dev" component={DevPortalPage} />
            <Route path="/dev/requests" component={DevRequestsPage} />
            <Route path="/dev/translations" component={TranslationPortalPage} />
            <Route path="*" component={NotFoundPage} />
          </Route>
        </Suspense>
      </Router>

      {/* Global notification container */}
      <ToastContainer />
    </AppErrorBoundary>
  );
}
